import { Collapsible as CollapsiblePrimitive } from '@base-ui/react/collapsible'
import { cn } from '@nt/ui/cn'
import { cva, type VariantProps } from 'class-variance-authority'

function Collapsible({ ...props }: CollapsiblePrimitive.Root.Props) {
  return <CollapsiblePrimitive.Root data-slot="collapsible" {...props} />
}

// `panel` is a rule-separated section, never a box: a card already holds it,
// and a bordered box there would be a card inside a card.
const collapsibleTriggerVariants = cva('', {
  variants: {
    variant: {
      plain: '',
      panel:
        'flex w-full items-center justify-between gap-3 border-b border-border py-3 text-left text-sm font-medium transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring',
      // The full-width header row of a tinted offer box (address upsell).
      row: 'flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-card',
    },
    tone: {
      default: '',
      brand: 'text-primary',
      attention: 'text-warning hover:text-warning',
    },
  },
  defaultVariants: { variant: 'plain', tone: 'default' },
})

function CollapsibleTrigger({
  className,
  variant,
  tone,
  ...props
}: CollapsiblePrimitive.Trigger.Props & VariantProps<typeof collapsibleTriggerVariants>) {
  return (
    <CollapsiblePrimitive.Trigger
      data-slot="collapsible-trigger"
      className={cn(collapsibleTriggerVariants({ variant, tone }), className)}
      {...props}
    />
  )
}

const collapsibleContentVariants = cva('', {
  variants: {
    variant: {
      plain: '',
      panel: 'space-y-4 pt-2 pb-4',
    },
    density: {
      default: '',
      relaxed: '',
    },
  },
  compoundVariants: [{ variant: 'panel', density: 'relaxed', class: 'space-y-6' }],
  defaultVariants: { variant: 'plain', density: 'default' },
})

function CollapsibleContent({
  className,
  variant,
  density,
  ...props
}: CollapsiblePrimitive.Panel.Props & VariantProps<typeof collapsibleContentVariants>) {
  return (
    <CollapsiblePrimitive.Panel
      data-slot="collapsible-content"
      className={cn(collapsibleContentVariants({ variant, density }), className)}
      {...props}
    />
  )
}

export { Collapsible, CollapsibleContent, CollapsibleTrigger }
